import * as React from 'react';
import * as uuid from 'uuid';

import { FormattedMessage, MessageDescriptor } from 'react-intl';

export function modifySet<T>(setT: Set<T>, key: T, to: boolean): boolean {
    // Ensures that this.has(key) === to
    // Returns true if a change was made to the set.

    let changed = false;
    if (to && !setT.has(key)) {
        setT.add(key);
        changed = true;
    } else if (!to) {
        changed = setT.delete(key);
    }
    return changed;
}

/**
 * Check if object has a key in a way Typescript likes.
 *
 * Otherwise when trying to access the value if the key exists on the object,
 * Typescript will complain that the key may not exist:
 *
 * if (hasKey(o, keyName)) { value = o[keyName]; }
 */
export function hasKey<O>(obj: O, key: string | number | symbol): key is keyof O {
    return key in obj;
}

interface WrappedMessageProps {
    message: MessageDescriptor;
    values?: Record<string, any>;
    children?: (message: string) => React.ReactNode;
}

// A convenience wrapper over FormattedMessage that lets us call it with a single message argument,
// instead of spreading a MessageDescriptor to props on each call.
// One of the early ideas was to use this to add pseudolocalization or middleware here,
// but this is not recommended now, because there are also many places that directly call `intl.formatMessage` or equivalent, thus bypassing this wrapper.
export const WrappedMessage = (props: WrappedMessageProps) => {
    const {message, ...other} = props;
    return (
        <FormattedMessage
            {...message}
            {...other}
        />
    );
};

// We need a wrapper for random uuids so it returns a constant value for unit tests.
export const uuid4 = () => {
    if (process.env.NODE_ENV === 'test') {
        return '387d4cd9-264c-4ffa-8aba-deb91c94220a';
    } else {
        return uuid.v4();
    }
};

export const languageLocaleProxy = {
    get<T extends {[key: string]: MessageDescriptor}>(
        target: { [key: string]: T }, key: string, receiver: any
    ): T {
        if (!(key in target)) {
            for (const loc of Object.keys(target)) {
                if (loc.toLowerCase().startsWith(key.toLowerCase())) return target[loc];
            }
            return target.en;
        }
        return target[key];
    }
};

export const getCookie = (name: string): string|null => {
    if (document.cookie === '') { return null; }
    const cookies = document.cookie.split(';');
    for (const pair of cookies) {
        const [key, val] = pair.split('=');
        if (key.trim() === name) {
            return val.trim();
        }
    }
    return null;
};

export function replicateElement(element: any, length: number) {
    const arr = [];
    for (let i = 0; i < length; i++) {
        arr.push(element);
    }
    return arr;
}


export function isURL(value: string): boolean {
    let urlValue;
    try {
        urlValue = new URL(value);
        return urlValue.protocol === 'http:' || urlValue.protocol === 'https:';
    } catch (_) {
        return false;
    }
}

/*
 * Returns the number of lines of text that can fit into element
 */
export function getMaxTextLinesInElement(element: HTMLElement): number {
    const styles = getComputedStyle(element);
    const divHeight = element.offsetHeight;
    let lineHeight = styles.lineHeight;
    if (lineHeight === 'normal') {
        lineHeight = styles.fontSize;
    }
    const lineHeightNumber = parseInt(lineHeight, 10);
    // tslint:disable-next-line:no-bitwise
    return ~~(divHeight / lineHeightNumber);
}

export function convertTimeZone(date: Date | string, tzString: string): Date {
    return new Date((typeof date === 'string' ? new Date(date) : date)
            .toLocaleString('en-US', {timeZone: tzString}));
}
